import axios from "axios";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Container } from "reactstrap";
import styled from "styled-components";
import packagePreview from "../../assets/img/package-preview.png";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { API_URL, DEMO_REPORT_URL } from "../../constants";
import {
  ACTION_COSTS,
  ADDABLE_ARTIFACTS,
  ARTIFACT_LABELS,
  CREDITS_PACK_KEY,
  FREE_MONTHLY_CREDITS,
  PACKS,
  PACK_EXPIRY_MONTHS,
  PACK_KEYS,
  PLANS,
  PLAN_LABELS,
  REGEN_COSTS,
  formatCreditsNumber,
  formatDollars,
  stashCreditsReturn,
} from "../../constants/credits";
import { authHeader, getToken } from "../../utils/auth";
import { storageGet, storageRemove, storageSet } from "../../utils/storage";

const PACKAGE_GROUPS = [
  {
    title: "Research",
    items: [
      "Property details and market analysis",
      "Comparable listings and a Quick CMA",
      "Buyer insights and neighborhood context",
    ],
  },
  {
    title: "Strategy",
    items: [
      "Pricing guidance",
      "Staging ideas and photo touch-ups",
      "Property-specific recommendations",
    ],
  },
  {
    title: "Presentation",
    items: [
      "Polished, personalized seller materials",
      "PDF, PowerPoint, and one-pager exports",
      "Social-ready listing snapshots",
    ],
  },
];

const WHY = [
  {
    icon: "clock",
    title: "Minutes, not hours",
    text: "Enter an address and your complete package lands in your inbox in minutes, not days.",
  },
  {
    icon: "edit",
    title: "Yours to shape",
    text: "Every section is editable — review, rewrite, and brand it before anything reaches a seller.",
  },
  {
    icon: "share",
    title: "Ready to share",
    text: "Publish a PDF, PowerPoint, one-pager, or social snapshots, plus share everything with one secure link.",
  },
];

const WhyIcon = (/** @type {{ name: string }} */ { name }) => {
  const p = {
    viewBox: "0 0 24 24",
    width: 22,
    height: 22,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: /** @type {"round"} */ ("round"),
    strokeLinejoin: /** @type {"round"} */ ("round"),
  };
  if (name === "clock")
    return (
      <svg {...p} aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  if (name === "edit")
    return (
      <svg {...p} aria-hidden="true">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
      </svg>
    );
  if (name === "share")
    return (
      <svg {...p} aria-hidden="true">
        <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
        <path d="M12 3v13" />
        <path d="M8 7l4-4 4 4" />
      </svg>
    );
  return null;
};

// Every number below comes from the catalog mirror in constants/credits.js;
// this file only decides how it reads.

const RESUME_KEY = "liq_resume_pricing_subscribe";
const n = formatCreditsNumber;

/** @typedef {{ kind: "plan" | "pack", key: string }} PricingProduct */

// The resume stash carries WHICH product was chosen. A bare "1" (the legacy
// stash) would buy the wrong product after sign-in, so it is dropped here.
/** @param {string | null} raw @returns {PricingProduct | null} */
const parseResume = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.kind === "plan" && PLANS[parsed.key]) return { kind: "plan", key: parsed.key };
    if (parsed?.kind === "pack" && PACKS[parsed.key]) return { kind: "pack", key: parsed.key };
  } catch {
    /* not JSON — a legacy flag */
  }
  return null;
};

// The menu, in user-facing names. Listing review is withheld until the
// product is defined (R5); everything else is what a listing can use. Keys
// come from the catalog (no artifact names are spelled here — the analytics
// contract test reads quoted `social_*` strings as event names).
/** @type {Record<string, string>} */
const MENU_UNITS = { photo_touchup: "per photo", video: "per video" };
const CREDIT_MENU = ["analysis", ...ADDABLE_ARTIFACTS, "photo_touchup", "video"].map((key) => ({
  key,
  label: ARTIFACT_LABELS[key],
  cost: ACTION_COSTS[key],
  unit: MENU_UNITS[key] || "",
}));

const FULL_SET_COST =
  ACTION_COSTS.analysis +
  ACTION_COSTS.branded_pdf +
  ACTION_COSTS.deck +
  ACTION_COSTS.one_pager +
  ACTION_COSTS.social_set;

const SCENARIO_PHOTOS = 10;

const SCENARIOS = [
  { label: "Listing Analysis on its own", cost: n(ACTION_COSTS.analysis) },
  {
    label: "Listing Analysis with a Branded PDF",
    cost: n(ACTION_COSTS.analysis + ACTION_COSTS.branded_pdf),
  },
  {
    label: "The full set — analysis, Branded PDF, Seller Presentation, One-pager, Social set",
    cost: n(FULL_SET_COST),
  },
  {
    label: `Photo touch-ups, ${ACTION_COSTS.photo_touchup} per photo (${SCENARIO_PHOTOS} photos)`,
    cost: n(ACTION_COSTS.photo_touchup * SCENARIO_PHOTOS),
  },
];

const CREDITS_FAQS = [
  {
    q: "What is a credit?",
    a: `A credit is the unit Listino uses to price the work it prepares. A Listing Analysis is ${n(ACTION_COSTS.analysis)} credits; a Branded PDF is ${n(ACTION_COSTS.branded_pdf)}; a photo touch-up is ${n(ACTION_COSTS.photo_touchup)} per photo. Credits only leave your balance when you start something, and the price is shown before you do.`,
  },
  {
    q: "What's always free?",
    a: "Editing, branding, comps curation, sharing, and publishing. Once something is prepared you can rewrite it, restyle it, re-export it, and republish it as often as you like without using a credit.",
  },
  {
    q: "What does the Free plan include?",
    a: `${n(FREE_MONTHLY_CREDITS)} credits every month, with no card required — enough for a Listing Analysis and a Branded PDF each month. Everything Listino prepares is available on every plan at the same credit prices. Files exported on the Free plan carry a small Listino mark; upgrading removes it from everything you've already prepared.`,
  },
  {
    q: "Monthly reset or annual pool?",
    a: "Monthly plans add their credits on each billing date and unused credits don't carry over. Annual plans are one pool for the whole year, used at your own pace. The Free plan resets monthly.",
  },
  {
    q: "Do credit packs expire?",
    a: `Packs are good for ${PACK_EXPIRY_MONTHS} months from purchase. If you're on a plan, they last through the end of your current term.`,
  },
  {
    q: "When is my card charged?",
    a: "Only when you buy a plan or a pack. Using your free monthly credits never involves a card. Plans renew automatically and can be canceled any time; packs are a one-time charge.",
  },
  {
    q: "Can I get a refund?",
    a: "Yes. Any plan or pack purchase can be refunded in full within 7 days of the purchase. A refund removes only the unspent credits from that purchase.",
  },
  {
    q: "What if something fails to prepare?",
    a: "Your credits come back automatically.",
  },
  {
    q: "How long does it take?",
    a: "About 10 minutes for a Listing Analysis. Files you add later take about a minute.",
  },
  {
    q: "Can I edit what Listino prepares?",
    a: "Yes — every listing prep is editable. Rewrite or hide sections, apply your branding, then publish exactly what you want to share.",
  },
];

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand-ink, #0b1c1e);
  text-align: center;
  margin: 0 0 26px;

  @media (max-width: 767.98px) {
    font-size: 28px;
    margin-bottom: 20px;
  }
`;

const PricingSection = styled.section`
  padding: 34px 0 24px;
`;

const PlansIntro = styled.p`
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: var(--brand-primary-700, #085552);
  margin: 0 0 30px;
`;

const IndividualCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 40px 34px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: ${(props) =>
    props.$featured
      ? "2px solid var(--brand-primary, #0e7e7a)"
      : "1px solid var(--brand-border, #e3ebec)"};
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  max-width: 900px;
  margin: 36px auto 0;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;

const PlanName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin-bottom: 6px;
`;

const PlanTagline = styled.p`
  font-size: 14px;
  color: var(--brand-ink-3, #5c7074);
  margin: 0 0 18px;
`;

const PriceDisplay = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 3px;

  span:first-of-type {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--brand-ink-3, #5c7074);
  }
`;

const PricePeriod = styled.span`
  font-size: 1.05rem;
  font-weight: 500;
  color: var(--brand-ink-3, #5c7074);
`;

const PriceNote = styled.p`
  font-size: 14px;
  color: var(--brand-ink-3, #5c7074);
  margin: 8px 0 22px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
`;

const Feature = styled.li`
  position: relative;
  padding: 7px 0 7px 28px;
  font-size: 15px;
  color: var(--brand-ink-2, #2b3f42);
  line-height: 1.45;

  &::before {
    content: "\u2713";
    position: absolute;
    left: 0;
    top: 7px;
    color: var(--brand-primary, #0e7e7a);
    font-weight: 700;
  }
`;

const CTAButton = styled(Button)`
  width: 100%;
  background: var(--brand-primary, #0e7e7a);
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--brand-primary-600, #0b6b68);
    transform: translateY(-1px);
    color: #fff;
  }

  &:disabled {
    opacity: 0.7;
  }
`;

const SubscribeError = styled.div`
  color: #d9534f;
  font-size: 0.9rem;
  margin-top: 10px;
`;

const Guarantee = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  margin: 32px auto 0;
  font-size: 15px;
  color: var(--brand-ink-2, #2b3f42);

  svg {
    color: var(--brand-primary, #0e7e7a);
    flex-shrink: 0;
  }

  strong {
    font-weight: 700;
    color: var(--brand-ink, #0b1c1e);
  }
`;

const IncludesSection = styled.section`
  padding: 92px 0 8px;
`;

const IncludesPanel = styled.div`
  background: var(--brand-bg-soft, #f7fafb);
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 20px;
  padding: 30px 34px 26px;
  max-width: 1080px;
  margin: 0 auto;

  @media (max-width: 767.98px) {
    padding: 24px 20px;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--brand-ink, #0b1c1e);
  text-align: center;
  margin-bottom: 14px;

  @media (max-width: 767.98px) {
    font-size: 26px;
  }
`;

const IncludesIntro = styled.p`
  font-size: 15.5px;
  line-height: 1.6;
  color: var(--brand-ink-3, #5c7074);
  margin: 0 0 18px;

  @media (max-width: 991.98px) {
    text-align: center;
  }
`;

const PreviewColumn = styled.div`
  min-width: 0;
`;

const IncludesLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 36px;
  align-items: center;
  margin: 0 auto;

  @media (max-width: 991.98px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const PreviewFigure = styled.figure`
  margin: 0;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const IncludesColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 26px;

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr;
  }
`;

const IncludeGroup = styled.div`
  min-width: 0;
`;

const IncludeGroupTitle = styled.h3`
  display: flex;
  align-items: baseline;
  gap: 9px;
  font-size: 16px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin: 0;
`;

const PreviewBadge = styled.span`
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--brand-primary-700, #085552);
  background: var(--brand-primary-50, #e6f3f2);
  border-radius: 999px;
  padding: 3px 11px;
  margin-bottom: 6px;
`;

const IncludeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
`;

const IncludeItem = styled.li`
  position: relative;
  padding: 5px 0 5px 22px;
  font-size: 13.5px;
  line-height: 1.45;
  color: var(--brand-ink-2, #2b3f42);

  &::before {
    content: "\u2713";
    position: absolute;
    left: 0;
    top: 6px;
    font-size: 12px;
    font-weight: 700;
    color: var(--brand-primary, #0e7e7a);
  }
`;

const SampleLink = styled.div`
  text-align: center;
  margin-top: 22px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: var(--brand-primary-700, #085552);
    text-decoration: none;
    border-bottom: 1px solid currentColor;
    padding-bottom: 2px;
  }

  a:hover {
    color: var(--brand-primary, #0e7e7a);
  }
`;

const WhySection = styled.section`
  padding: 76px 0;
  background: var(--brand-bg-soft, #f7fafb);
`;

const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 32px;
  max-width: 920px;
  margin: 0 auto;
`;

const WhyCard = styled.div`
  text-align: center;
`;

const WhyIconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--brand-primary-50, #e6f3f2);
  color: var(--brand-primary, #0e7e7a);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
`;

const WhyTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin-bottom: 10px;
`;

const WhyText = styled.p`
  font-size: 15px;
  line-height: 1.55;
  color: var(--brand-ink-3, #5c7074);
  margin: 0;
`;

const FAQSection = styled.section`
  padding: 76px 0 100px;
  background: #fff;
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: #fff;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 12px;
  padding: 24px 26px;
  margin-bottom: 14px;
`;

const FAQQuestion = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin: 0 0 10px;
`;

const FAQAnswer = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: var(--brand-ink-3, #5c7074);
  margin: 0;
`;

// --- credits-only layout pieces -------------------------------------------

const ThreePlansGrid = styled(PlansGrid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));
  max-width: 1080px;
  gap: 22px;

  @media (max-width: 991.98px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;

const IntervalToggle = styled.fieldset`
  display: inline-flex;
  margin: 0;
  min-width: 0;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 999px;
  padding: 3px;
  background: var(--brand-bg-soft, #f7fafb);

  legend {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;

const ToggleWrap = styled.div`
  text-align: center;
`;

const ToggleButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${(props) => (props["aria-pressed"] ? "#fff" : "transparent")};
  color: ${(props) =>
    props["aria-pressed"] ? "var(--brand-ink, #0b1c1e)" : "var(--brand-ink-3, #5c7074)"};
  box-shadow: ${(props) => (props["aria-pressed"] ? "0 1px 4px rgba(0, 0, 0, 0.08)" : "none")};
`;

const ResetLine = styled.p`
  text-align: center;
  font-size: 14px;
  line-height: 1.55;
  color: var(--brand-ink-3, #5c7074);
  max-width: 640px;
  margin: 22px auto 0;
`;

const PacksBlock = styled.div`
  max-width: 720px;
  margin: 56px auto 0;
`;

const PacksTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  text-align: center;
  margin-bottom: 6px;
`;

const PacksNote = styled.p`
  text-align: center;
  font-size: 14px;
  color: var(--brand-ink-3, #5c7074);
  margin: 0 0 18px;
`;

const PackList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 14px;
  overflow: hidden;
`;

const PackRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  font-size: 15px;
  color: var(--brand-ink-2, #2b3f42);
  background: #fff;

  & + & {
    border-top: 1px solid var(--brand-border, #e3ebec);
  }

  strong {
    color: var(--brand-ink, #0b1c1e);
  }
`;

const PackPrice = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: var(--brand-ink, #0b1c1e);
`;

const PackButton = styled(CTAButton)`
  width: auto;
  padding: 9px 18px;
  font-size: 14px;
`;

const MenuSection = styled.section`
  padding: 72px 0 8px;
`;

const MenuIntro = styled.p`
  text-align: center;
  font-size: 15.5px;
  line-height: 1.6;
  color: var(--brand-ink-3, #5c7074);
  max-width: 640px;
  margin: 0 auto 30px;
`;

const MenuLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  max-width: 980px;
  margin: 0 auto;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-width: 560px;
  }
`;

const MenuCard = styled.div`
  background: var(--brand-bg-soft, #f7fafb);
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 16px;
  padding: 24px 26px;
`;

const MenuTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: var(--brand-ink, #0b1c1e);
  margin: 0 0 12px;
`;

const MenuList = styled.dl`
  margin: 0;
`;

const MenuRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  font-size: 15px;
  color: var(--brand-ink-2, #2b3f42);
  border-top: 1px solid var(--brand-border, #e3ebec);

  &:first-child {
    border-top: none;
  }

  dt {
    font-weight: 500;
    margin: 0;
  }

  dd {
    margin: 0;
    white-space: nowrap;
    color: var(--brand-ink, #0b1c1e);
    font-weight: 600;
  }

  small {
    font-weight: 400;
    color: var(--brand-ink-3, #5c7074);
  }
`;

const MenuFoot = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: var(--brand-ink-3, #5c7074);
  margin: 14px 0 0;
`;

const SampleAside = styled.p`
  text-align: center;
  font-size: 14px;
  color: var(--brand-ink-3, #5c7074);
  margin: 10px 0 0;
`;

/**
 * The page as it ships while the credits flag is dark: $20 a listing or the
 * $199 annual plan. Deleted at the launch flip, not before.
 */
const NewPricingPage = () => {
  const history = useHistory();
  const [billingInterval, setBillingInterval] = useState(/** @type {"month" | "year"} */ ("month"));
  // The product key whose checkout is in flight (one button disables at a time).
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));
  const [checkoutError, setCheckoutError] = useState(/** @type {string | null} */ (null));
  // The signed-in account's plan, from /auth/me/ credits.plan_key. Null when
  // signed out or unknown (a failed lookup, or the API flag still dark) — the
  // page then sells nothing as "current" and lets the server be the judge.
  const [planKey, setPlanKey] = useState(/** @type {string | null} */ (null));
  const [accountChecked, setAccountChecked] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      setAccountChecked(true);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/auth/me/`, { headers: authHeader() })
      .then((resp) => {
        const key = resp.data?.credits?.plan_key;
        if (!cancelled) setPlanKey(typeof key === "string" ? key : null);
      })
      .catch(() => {
        /* signed out or unreachable — every button stays available */
      })
      .finally(() => {
        if (!cancelled) setAccountChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** @param {PricingProduct} product */
  const startCheckout = (product) => {
    setCheckoutError(null);
    setBusyKey(product.key);
    const pendingCoupon = (storageGet("pendingCouponCode") || "").trim();
    let request;
    if (product.kind === "plan") {
      const plan = PLANS[product.key];
      request = axios.post(
        `${API_URL}/subscribe/`,
        {
          plan: plan.product,
          interval: plan.interval,
          ...(pendingCoupon ? { coupon_code: pendingCoupon } : {}),
        },
        { headers: authHeader() }
      );
    } else {
      request = axios.post(
        `${API_URL}/credits/checkout/`,
        { pack: product.key },
        { headers: authHeader() }
      );
    }
    request
      .then(({ data }) => {
        if (data?.stripe_url) {
          if (pendingCoupon && product.kind === "plan") storageRemove("pendingCouponCode");
          // The return rails are for a session that exists: a guest's account
          // is created by the webhook, and the receipt tells them to sign in
          // — there is nowhere to "return" to, so nothing is stashed.
          if (getToken()) {
            if (product.kind === "pack") storageSet(CREDITS_PACK_KEY, product.key);
            // Stripe's cancel and success legs come back here, not to a listing.
            stashCreditsReturn({ path: "/pricing" });
          }
          window.location = data.stripe_url;
        } else {
          setCheckoutError("Could not start checkout. Please try again.");
          setBusyKey(null);
        }
      })
      .catch((err) => {
        const response = /** @type {{response?: {status?: number, data?: {error?: string}}}} */ (
          err
        ).response;
        if (response?.status === 401) {
          setCheckoutError("Your session expired. Sign in again to continue.");
        } else if (response?.status === 409 && response.data?.error) {
          setCheckoutError(response.data.error);
        } else if (response?.status === 429) {
          // Guests are throttled per IP; state it and stop.
          setCheckoutError("Too many attempts. Try again in a minute.");
        } else {
          setCheckoutError("Checkout is unavailable right now. Please try again later.");
        }
        setBusyKey(null);
      });
  };

  /** @param {PricingProduct} product */
  const choose = (product) => {
    // Signed in or out, the button is the purchase (Trello #53). The
    // sign-in-first resume below is kept only for a stash a visitor may
    // still be carrying from before; nothing here writes one any more.
    startCheckout(product);
  };

  // One-shot resume after the sign-in detour. Waits for the account lookup so
  // someone who parked a plan they turn out to already hold lands on "Your
  // current plan" instead of auto-firing a duplicate subscribe.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot, keyed on the account check resolving
  useEffect(() => {
    if (!accountChecked) return;
    const raw = storageGet(RESUME_KEY);
    if (!raw || !getToken()) return;
    storageRemove(RESUME_KEY);
    const product = parseResume(raw);
    if (!product) return;
    if (product.kind === "plan" && product.key === planKey) return;
    startCheckout(product);
    // `planKey` is read but deliberately not a dependency: this must fire once
    // when the account check resolves. CRA's build lints with ESLint, which
    // does not read the biome-ignore above, so both suppressions are needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountChecked]);

  const goToSubmit = () => {
    history.push("/verify");
  };

  const signedIn = !!getToken();
  const onFreePlan = signedIn && planKey === "free";

  /** @param {"agent" | "pro"} product */
  const planCard = (product) => {
    const key = `${product}_${billingInterval}`;
    const plan = PLANS[key];
    const isCurrent = signedIn && planKey === key;
    const yearly = billingInterval === "year";
    return (
      <IndividualCard $featured={product === "agent"} key={product}>
        <PlanName>{PLAN_LABELS[product]}</PlanName>
        <PlanTagline>
          {product === "agent" ? "For agents who list most months." : "For teams and busy agents."}
        </PlanTagline>
        <PriceDisplay>
          <span>$</span>
          {formatDollars(plan.amountCents).slice(1)}
          <PricePeriod>{yearly ? "/year" : "/month"}</PricePeriod>
        </PriceDisplay>
        <PriceNote>
          {n(plan.credits)} credits {yearly ? "for the year, in one pool" : "every month"}
        </PriceNote>
        <FeaturesList>
          <Feature>Everything Listino prepares, at the same credit prices</Feature>
          <Feature>No Listino mark on your files</Feature>
          <Feature>
            {yearly ? "Use the pool at your own pace" : "Credits reset on each billing date"}
          </Feature>
          <Feature>Renews automatically, cancel anytime</Feature>
        </FeaturesList>
        {isCurrent ? (
          <CTAButton size="lg" disabled>
            Your current plan
          </CTAButton>
        ) : (
          <CTAButton
            size="lg"
            disabled={busyKey === key}
            onClick={() => choose({ kind: "plan", key })}
          >
            {busyKey === key ? "Starting\u2026" : `Choose ${PLAN_LABELS[product]}`}
          </CTAButton>
        )}
      </IndividualCard>
    );
  };

  return (
    <PageContainer>
      <NewNavbar />

      <IncludesSection>
        <Container>
          <IncludesPanel>
            <PageTitle>A complete listing package, ready in minutes.</PageTitle>
            <IncludesLayout>
              <PreviewColumn>
                <IncludesIntro>
                  From property analysis and pricing to seller-ready materials and photo
                  enhancements, all editable and branded to you.
                </IncludesIntro>
                <PreviewFigure>
                  <PreviewBadge>4 formats · 20+ pages</PreviewBadge>
                  <img
                    src={packagePreview}
                    alt="A Listino listing package: the listing prep, a one-pager, a presentation slide, and a social post"
                  />
                </PreviewFigure>
                <SampleLink>
                  <a href={DEMO_REPORT_URL} target="_blank" rel="noopener noreferrer">
                    See a full sample package (PDF)
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <path d="M15 3h6v6" />
                      <path d="M10 14L21 3" />
                    </svg>
                  </a>
                  <SampleAside>or prepare your own — free to start.</SampleAside>
                </SampleLink>
              </PreviewColumn>

              <IncludesColumns>
                {PACKAGE_GROUPS.map((group) => (
                  <IncludeGroup key={group.title}>
                    <IncludeGroupTitle>{group.title}</IncludeGroupTitle>
                    <IncludeList>
                      {group.items.map((item) => (
                        <IncludeItem key={item}>{item}</IncludeItem>
                      ))}
                    </IncludeList>
                  </IncludeGroup>
                ))}
              </IncludesColumns>
            </IncludesLayout>
          </IncludesPanel>
        </Container>
      </IncludesSection>

      <PricingSection>
        <Container>
          <SectionTitle>Every account starts free.</SectionTitle>
          <PlansIntro>
            {n(FREE_MONTHLY_CREDITS)} credits a month, no card required. Plans and packs add more
            when you need them.
          </PlansIntro>

          <ToggleWrap>
            <IntervalToggle>
              <legend>Billing period</legend>
              <ToggleButton
                type="button"
                aria-pressed={billingInterval === "month"}
                onClick={() => setBillingInterval("month")}
              >
                Monthly
              </ToggleButton>
              <ToggleButton
                type="button"
                aria-pressed={billingInterval === "year"}
                onClick={() => setBillingInterval("year")}
              >
                Yearly
              </ToggleButton>
            </IntervalToggle>
          </ToggleWrap>

          <ThreePlansGrid data-testid="plans-grid">
            <IndividualCard>
              <PlanName>{PLAN_LABELS.free}</PlanName>
              <PlanTagline>Every account starts here.</PlanTagline>
              <PriceDisplay>
                <span>$</span>0<PricePeriod>/month</PricePeriod>
              </PriceDisplay>
              <PriceNote>{n(FREE_MONTHLY_CREDITS)} credits every month</PriceNote>
              <FeaturesList>
                <Feature>Everything Listino prepares, at the same credit prices</Feature>
                <Feature>Editing, branding, comps, sharing, and publishing — always free</Feature>
                <Feature>Files carry a small Listino mark</Feature>
                <Feature>No card required</Feature>
              </FeaturesList>
              {onFreePlan ? (
                <CTAButton size="lg" disabled>
                  Your current plan
                </CTAButton>
              ) : (
                <CTAButton onClick={goToSubmit} size="lg">
                  Start free
                </CTAButton>
              )}
            </IndividualCard>
            {planCard("agent")}
            {planCard("pro")}
          </ThreePlansGrid>
          {checkoutError && (
            <SubscribeError role="alert" style={{ textAlign: "center" }}>
              {checkoutError}
            </SubscribeError>
          )}

          <ResetLine>
            Monthly plans add their credits on each billing date, and unused credits don't carry
            over. Yearly plans are one pool for the whole year, used at your own pace.
          </ResetLine>

          <PacksBlock>
            <PacksTitle>Credit packs</PacksTitle>
            <PacksNote>
              Good for {PACK_EXPIRY_MONTHS} months. On a plan, a pack lasts through your current
              term.
            </PacksNote>
            <PackList>
              {PACK_KEYS.map((key) => {
                const pack = PACKS[key];
                return (
                  <PackRow key={key}>
                    <strong>{n(pack.credits)} credits</strong>
                    <PackPrice>{formatDollars(pack.amountCents)}</PackPrice>
                    <PackButton
                      size="sm"
                      disabled={busyKey === key}
                      onClick={() => choose({ kind: "pack", key })}
                      aria-label={`Buy ${n(pack.credits)} credits for ${formatDollars(pack.amountCents)}`}
                    >
                      {busyKey === key ? "Starting\u2026" : "Buy"}
                    </PackButton>
                  </PackRow>
                );
              })}
            </PackList>
          </PacksBlock>

          <Guarantee>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <span>
              <strong>
                Try it free — {n(FREE_MONTHLY_CREDITS)} credits a month. No card required.
              </strong>{" "}
              Full refund within 7 days of any plan or pack purchase.
            </span>
          </Guarantee>
        </Container>
      </PricingSection>

      <MenuSection>
        <Container>
          <SectionTitle>What things cost</SectionTitle>
          <MenuIntro>
            A credit is Listino's unit for the work it prepares. Editing, branding, comps, sharing,
            and publishing never use credits.
          </MenuIntro>
          <MenuLayout>
            <MenuCard>
              <MenuTitle>The menu</MenuTitle>
              <MenuList>
                {CREDIT_MENU.map((item) => (
                  <MenuRow key={item.key}>
                    <dt>{item.label}</dt>
                    <dd>
                      {n(item.cost)} credits{item.unit ? <small> {item.unit}</small> : null}
                    </dd>
                  </MenuRow>
                ))}
              </MenuList>
              <MenuFoot>
                The Listing Analysis is the Listing Strategy, Quick CMA, Listing Description, photo
                review, and a shareable web page. Files can be added when you prepare a listing or
                any time after, at the same price.
              </MenuFoot>
            </MenuCard>
            <MenuCard>
              <MenuTitle>What a listing costs</MenuTitle>
              <MenuList>
                {SCENARIOS.map((item) => (
                  <MenuRow key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.cost} credits</dd>
                  </MenuRow>
                ))}
              </MenuList>
              <MenuFoot>
                The Free plan's {n(FREE_MONTHLY_CREDITS)} credits cover a Listing Analysis and a
                Branded PDF every month. Re-preparing a listing is free the first time, then{" "}
                {n(REGEN_COSTS.analysis)} credits.
              </MenuFoot>
            </MenuCard>
          </MenuLayout>
        </Container>
      </MenuSection>

      <WhySection>
        <Container>
          <SectionTitle>Why agents use Listino</SectionTitle>
          <WhyGrid>
            {WHY.map((item) => (
              <WhyCard key={item.title}>
                <WhyIconWrap>
                  <WhyIcon name={item.icon} />
                </WhyIconWrap>
                <WhyTitle>{item.title}</WhyTitle>
                <WhyText>{item.text}</WhyText>
              </WhyCard>
            ))}
          </WhyGrid>
        </Container>
      </WhySection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently asked questions</SectionTitle>
          <FAQContainer>
            {CREDITS_FAQS.map((faq) => (
              <FAQItem key={faq.q}>
                <FAQQuestion>{faq.q}</FAQQuestion>
                <FAQAnswer>{faq.a}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <NewFooter />
    </PageContainer>
  );
};

export default NewPricingPage;
